import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useMemo, useRef, useEffect } from 'react';
import * as THREE from 'three';
import type { GameState } from '@/types/game';
import { isWebGLAvailable } from '@/utils/webgl';
import { getWorldForLevel } from '@/config/worlds';

interface ProceduralBackgroundProps {
  level: number;
  gameState: GameState;
}

// Shader for procedural clouds/terrain
const vertexShader = `
  varying vec2 vUv;
  varying float vElevation;
  uniform float uTime;
  uniform float uWaveFrequency;
  uniform float uWaveAmplitude;
  
  void main() {
    vUv = uv;
    
    vec3 pos = position;
    float elevation = sin(pos.x * uWaveFrequency + uTime) * uWaveAmplitude;
    elevation += sin(pos.y * uWaveFrequency * 0.5 + uTime * 0.5) * uWaveAmplitude * 0.5;
    
    pos.z += elevation;
    vElevation = elevation;
    
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`;

const fragmentShader = `
  varying vec2 vUv;
  varying float vElevation;
  uniform vec3 uColorA;
  uniform vec3 uColorB;
  uniform vec3 uColorC;
  uniform float uTime;
  
  void main() {
    float mixStrength = (vElevation + 0.1) * 2.0;
    vec3 color = mix(uColorA, uColorB, vUv.y);
    color = mix(color, uColorC, mixStrength);
    
    // Add some animated noise
    float noise = sin(vUv.x * 10.0 + uTime) * sin(vUv.y * 10.0 + uTime * 0.5) * 0.05;
    color += noise;
    
    gl_FragColor = vec4(color, 0.95);
  }
`;

// Particle field component
function ParticleField({ count = 1000, theme }: { count?: number; theme: string }) {
  const points = useRef<THREE.Points>(null);
  const time = useRef(0);
  
  const [positions, colors] = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    
    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      positions[i3] = (Math.random() - 0.5) * 50;
      positions[i3 + 1] = (Math.random() - 0.5) * 50;
      positions[i3 + 2] = (Math.random() - 0.5) * 10;
      
      // Color based on theme
      const intensity = Math.random();
      if (theme === 'frostline') {
        colors[i3] = 0.7 + intensity * 0.3;
        colors[i3 + 1] = 0.9 + intensity * 0.1;
        colors[i3 + 2] = 1;
      } else if (theme === 'greenwood') {
        colors[i3] = 0.2 + intensity * 0.3;
        colors[i3 + 1] = 0.6 + intensity * 0.2;
        colors[i3 + 2] = 0.2;
      } else if (theme === 'stonehollow') {
        colors[i3] = 0.55 + intensity * 0.2;
        colors[i3 + 1] = 0.6 + intensity * 0.2;
        colors[i3 + 2] = 0.7 + intensity * 0.18;
      } else if (theme === 'emberdeep') {
        colors[i3] = 0.9 + intensity * 0.1;
        colors[i3 + 1] = 0.45 + intensity * 0.2;
        colors[i3 + 2] = 0.3 + intensity * 0.16;
      } else if (theme === 'arcana') {
        colors[i3] = 0.55 + intensity * 0.3;
        colors[i3 + 1] = 0.5 + intensity * 0.28;
        colors[i3 + 2] = 0.95;
      } else {
        colors[i3] = intensity;
        colors[i3 + 1] = intensity;
        colors[i3 + 2] = intensity;
      }
    }
    
    return [positions, colors];
  }, [count, theme]);
  
  useFrame((_, delta) => {
    if (!points.current) return;
    
    time.current += delta;
    const positions = points.current.geometry.attributes.position.array as Float32Array;
    
    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      
      // Float particles upward
      positions[i3 + 1] += delta * 0.5;
      
      // Add some wave motion
      positions[i3] += Math.sin(time.current + i) * delta * 0.1;
      
      // Reset if too high
      if (positions[i3 + 1] > 25) {
        positions[i3 + 1] = -25;
        positions[i3] = (Math.random() - 0.5) * 50;
      }
    }
    
    points.current.geometry.attributes.position.needsUpdate = true;
  });
  
  return (
    <points ref={points}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positions, 3]}
        />
        <bufferAttribute
          attach="attributes-color"
          args={[colors, 3]}
        />
      </bufferGeometry>
      <pointsMaterial size={0.1} vertexColors opacity={0.6} transparent />
    </points>
  );
}

// Animated background mesh
function BackgroundMesh({ theme }: { theme: string }) {
  const mesh = useRef<THREE.Mesh>(null);
  const uniforms = useRef({
    uTime: { value: 0 },
    uColorA: { value: new THREE.Color('#667eea') },
    uColorB: { value: new THREE.Color('#764ba2') },
    uColorC: { value: new THREE.Color('#f093fb') },
    uWaveFrequency: { value: 0.2 },
    uWaveAmplitude: { value: 0.1 },
  });
  
  // Update colors based on theme
  useEffect(() => {
    switch (theme) {
      case 'greenwood':
        uniforms.current.uColorA.value.set('#84fab0');
        uniforms.current.uColorB.value.set('#8fd3f4');
        uniforms.current.uColorC.value.set('#b8e986');
        break;
      case 'stonehollow':
        uniforms.current.uColorA.value.set('#7b8796');
        uniforms.current.uColorB.value.set('#5b6678');
        uniforms.current.uColorC.value.set('#b0becf');
        break;
      case 'frostline':
        uniforms.current.uColorA.value.set('#cbefff');
        uniforms.current.uColorB.value.set('#78d2f0');
        uniforms.current.uColorC.value.set('#e9fbff');
        break;
      case 'emberdeep':
        uniforms.current.uColorA.value.set('#ffba74');
        uniforms.current.uColorB.value.set('#ff8d5c');
        uniforms.current.uColorC.value.set('#ff614d');
        break;
      case 'arcana':
        uniforms.current.uColorA.value.set('#2c3e50');
        uniforms.current.uColorB.value.set('#6958d7');
        uniforms.current.uColorC.value.set('#4cc6f7');
        break;
      default:
        uniforms.current.uColorA.value.set('#667eea');
        uniforms.current.uColorB.value.set('#764ba2');
        uniforms.current.uColorC.value.set('#f093fb');
    }
  }, [theme]);
  
  useFrame((state) => {
    if (mesh.current) {
      uniforms.current.uTime.value = state.clock.elapsedTime * 0.5;
    }
  });
  
  return (
    <mesh ref={mesh} position={[0, 0, -5]} rotation={[-Math.PI / 6, 0, 0]}>
      <planeGeometry args={[50, 50, 128, 128]} />
      <shaderMaterial
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms.current}
        transparent
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

// Camera controller for subtle movement
function CameraController() {
  const { camera } = useThree();
  const time = useRef(0);
  
  useFrame((_, delta) => {
    time.current += delta;
    
    // Subtle camera movement
    camera.position.x = Math.sin(time.current * 0.2) * 0.5;
    camera.position.y = Math.cos(time.current * 0.15) * 0.3;
    camera.lookAt(0, 0, 0);
  });
  
  return null;
}

// Main component
const ProceduralBackground: React.FC<ProceduralBackgroundProps> = ({ level, gameState }) => {
  const world = useMemo(() => getWorldForLevel(level), [level]);
  const theme = useMemo(() => {
    if (gameState.isTutorial) return 'greenwood';
    return world.id;
  }, [gameState.isTutorial, world.id]);

  const webglAvailable = useMemo(() => isWebGLAvailable(), []);
  const fallbackBackground = useMemo(() => {
    switch (theme) {
      case 'greenwood':
        return 'linear-gradient(135deg, rgba(132, 250, 176, 0.18) 0%, rgba(143, 211, 244, 0.18) 100%)';
      case 'stonehollow':
        return 'linear-gradient(135deg, rgba(123, 135, 150, 0.2) 0%, rgba(87, 98, 112, 0.2) 100%)';
      case 'frostline':
        return 'linear-gradient(135deg, rgba(190, 238, 255, 0.2) 0%, rgba(111, 199, 233, 0.18) 100%)';
      case 'emberdeep':
        return 'linear-gradient(135deg, rgba(255, 174, 102, 0.22) 0%, rgba(255, 94, 77, 0.18) 100%)';
      case 'arcana':
        return 'radial-gradient(circle at 50% 30%, rgba(108, 99, 255, 0.26) 0%, rgba(64, 82, 190, 0.18) 40%, rgba(0, 0, 0, 0) 70%)';
      default:
        return 'linear-gradient(135deg, rgba(102, 126, 234, 0.18) 0%, rgba(240, 147, 251, 0.18) 100%)';
    }
  }, [theme]);
  
  // Use worker for heavy computations
  const workerRef = useRef<Worker | undefined>(undefined);
  
  useEffect(() => {
    // Worker initialization commented out for now to fix TypeScript errors
    // TODO: Implement worker setup with proper Vite configuration
    // if (typeof Worker !== 'undefined') {
    //   workerRef.current = new Worker(
    //     new URL('../../workers/gameWorker.ts', import.meta.url),
    //     { type: 'module' }
    //   );
    
    //   // Generate terrain data
    //   workerRef.current.postMessage({
    //     type: 'GENERATE_TERRAIN',
    //     data: { width: 128, height: 128, scale: 0.05 }
    //   });
    // }
    
    return () => {
      workerRef.current?.terminate();
    };
  }, []);
  
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -10,
      }}
    >
      {webglAvailable ? (
        <Canvas
          camera={{ position: [0, 0, 10], fov: 60 }}
          gl={{
            antialias: false,
            alpha: true,
            powerPreference: 'high-performance',
            stencil: false,
            depth: false,
          }}
          dpr={[1, 2]}
        >
          <CameraController />
          <BackgroundMesh theme={theme} />
          <ParticleField theme={theme} count={theme === 'arcana' ? 1800 : 600} />

          {/* Ambient lighting */}
          <ambientLight intensity={0.5} />
          <pointLight position={[10, 10, 10]} intensity={0.5} />
        </Canvas>
      ) : (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background: fallbackBackground,
          }}
        />
      )}
    </div>
  );
};

export default ProceduralBackground;
